"use client"

import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import {
  Home,
  Factory,
  Building2,
  Users,
  MonitorCheck,
  CircleDollarSign,
  BarChartIcon as ChartNoAxesCombined,
  DoorOpen,
  ChevronRight,
  TicketIcon as TicketsPlane,
  BookUp2,
  MenuIcon,
  X,
  UserRound,
  IdCard,
  CalendarClock,
  Tags,
} from "lucide-react"
import "./Menu.css"

const rolePermissions = {
  DEVELOPER: [
    "inicio",
    "empresa",
    "sucursal",
    "tarifas",
    "usuarios",
    "control",
    "recaudaciones",
    "estadistica",
    "informe",
    "membresias",
    "clientes",
    "calendario",
    "tipos-tarifa",
    "salir",
  ],
  ADMIN: ["inicio", "sucursal", "usuarios", "control", "recaudaciones", "estadistica", "informe", "membresias", "clientes", "salir"],
  USERS: ["inicio", "control", "salir"],
}

const menuItems = [
  { id: "inicio", name: "Inicio", icon: <Home className="menu-icon" />, path: "/home" },
  { id: "empresa", name: "Empresa", icon: <Factory className="menu-icon" />, path: "/empresa" },
  { id: "sucursal", name: "Sucursal", icon: <Building2 className="menu-icon" />, path: "/sucursal" },
  { id: "tarifas", name: "Tarifas", icon: <TicketsPlane className="menu-icon" />, path: "/tarifas" },
  { id: "usuarios", name: "Usuarios", icon: <Users className="menu-icon" />, path: "/empleado" },
  { id: "control", name: "Control de Registros", icon: <MonitorCheck className="menu-icon" />, path: "/control" },
  { id: "clientes", name: "Clientes", icon: <UserRound className="menu-icon" />, path: "/clientes" },
  { id: "membresias", name: "Membresias", icon: <IdCard  className="menu-icon" />, path: "/membresias" },
  { id: "calendario", name: "Calendario", icon: <CalendarClock className="menu-icon" />, path: "/calendario" },
  { id: "tipos-tarifa", name: "Tipos de Tarifa", icon: <Tags className="menu-icon" />, path: "/tipos-tarifa" },
  {
    id: "recaudaciones",
    name: "Recaudaciones",
    icon: <CircleDollarSign className="menu-icon" />,
    path: "/recaudaciones",
  },
  { id: "estadistica", name: "Estadistica", icon: <ChartNoAxesCombined className="menu-icon" />, path: "/estadistica" },
  { id: "salir", name: "Salir", icon: <DoorOpen className="menu-icon" /> },
]

const Menu = ({ selectedMenu, onMenuClick }) => {
  const navigate = useNavigate()
  const [role, setRole] = useState(localStorage.getItem("role"))
  const [expanded, setExpanded] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const sidebarRef = useRef(null)

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
      // Auto-collapse sidebar on mobile
      if (window.innerWidth <= 768) {
        setExpanded(false)
      }
    }

    checkMobile()
    window.addEventListener("resize", checkMobile)

    return () => {
      window.removeEventListener("resize", checkMobile)
    }
  }, [])

  useEffect(() => {
    const handleStorageChange = () => {
      setRole(localStorage.getItem("role"))
    }

    window.addEventListener("storage", handleStorageChange)

    // Close sidebar when clicking outside on mobile
    const handleClickOutside = (event) => {
      if (isMobile && expanded && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setExpanded(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("touchstart", handleClickOutside)

    return () => {
      window.removeEventListener("storage", handleStorageChange)
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("touchstart", handleClickOutside)
    }
  }, [isMobile, expanded])

  const handleClick = (item) => {
    if (item.id === "salir") {
      localStorage.removeItem("token")
      localStorage.removeItem("idEmpleado")
      localStorage.removeItem("nombreEmpleado")
      localStorage.removeItem("role")
      localStorage.removeItem("idempresa")
      localStorage.removeItem("idsucursal")
      setRole(null)
      navigate("/")
    } else {
      onMenuClick(item.name)
      navigate(item.path)
      // Close sidebar after navigation on mobile
      if (isMobile) {
        setExpanded(false)
      }
    }
  }

  // Toggle sidebar function
  const toggleSidebar = (e) => {
    e.stopPropagation()
    setExpanded(!expanded)
  }

  return (
    <>
      {/* Mobile menu toggle button - repositioned based on menu state */}
      <button
        className={`mobile-menu-toggle ${expanded ? "active" : ""}`}
        onClick={toggleSidebar}
        aria-label="Toggle menu"
      >
        {expanded ? <X size={20} /> : <MenuIcon size={24} />}
      </button>

      <aside ref={sidebarRef} className={`sidebar ${expanded ? "expanded" : "collapsed"} ${isMobile ? "mobile" : ""}`}>
        {!isMobile && (
          <div className="toggle-indicator" onClick={toggleSidebar}>
            <ChevronRight className={`toggle-icon ${expanded ? "rotate" : ""}`} />
          </div>
        )}

        <div className="sidebar-items-container">
          {menuItems
            .filter((item) => rolePermissions[role]?.includes(item.id))
            .map((item) => (
              <div
                key={item.id}
                className={`sidebar-item ${selectedMenu === item.name ? "active" : ""}`}
                onClick={() => handleClick(item)}
              >
                {item.icon}
                <span className="sidebar-item-text">{item.name}</span>
              </div>
            ))}
        </div>
      </aside>

      {/* Overlay for mobile */}
      {isMobile && expanded && <div className="sidebar-overlay" onClick={() => setExpanded(false)}></div>}
    </>
  )
}

export default Menu
